/**
 * Created by sammy on 1/21/20.
 * Project: webapp-template. File: FormComponents
 */
import * as React from "react";
import {
    Box,
    Checkbox,
    CheckboxProps,
    FilledTextFieldProps,
    FormControl,
    InputAdornment,
    InputLabel,
    SelectProps,
    TextField,
    Theme,
    Select,
    OutlinedInput,
    FormHelperText,
    MenuItem,
    Chip,
    FilledInput,
    StandardTextFieldProps,
    OutlinedTextFieldProps,
    Radio,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Typography,
    CircularProgress,
    SelectChangeEvent,
    AutocompleteRenderInputParams,
    AutocompleteChangeReason,
    AutocompleteChangeDetails,
    Autocomplete,
    Card,
    IconButton,
    Switch,
    SwitchProps,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { FieldProps, useField, useFormikContext } from "formik";
import { observer } from "mobx-react-lite";
import { PagerMeta } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { ServerListData } from "../../modules/core/data/ListData";
import { ChangeEvent, KeyboardEvent, ReactNode, SyntheticEvent, useCallback, useEffect, useRef, useState } from "react";
import { MobileTimePicker, MobileTimePickerProps, MobileDateTimePicker, MobileDateTimePickerProps } from "@mui/x-date-pickers";

import { AutocompleteProps } from "@mui/material";
import { SelectableCard, SelectableCardProps } from "../card/SelectableCard";
import { useDropzone } from "react-dropzone";
import { CloseIcon } from "../CommonIcons";
import { useMountEffect } from "../hooks/hookslib";
import { useTranslation } from "react-i18next";
import { getPickerValidationMessage } from "../../modules/i18n/pickerValidation";
import { getFileDropErrorMessage } from "./fileDropValidation";

const useLocalizedField = (name: string) => {
    const result = useField(name);
    const [, meta] = result;
    const { validateField } = useFormikContext();
    const { i18n } = useTranslation();
    const language = i18n.resolvedLanguage ?? i18n.language;
    const previousLanguage = useRef(language);

    useEffect(() => {
        if (previousLanguage.current === language) {
            return;
        }

        previousLanguage.current = language;
        if (meta.touched) {
            void validateField(name);
        }
    }, [language, meta.touched, name, validateField]);

    return result;
};

// ======================
// FormikTextField
// ======================
interface FormTextFieldProps {
    label: string;
    name: string;
    icon?: React.ReactElement;
    helperText?: React.ReactNode;
    overrideInitialValue?: boolean;
}

export const FormTextField: React.FC<
    FormTextFieldProps & Partial<OutlinedTextFieldProps | FilledTextFieldProps | StandardTextFieldProps> & Partial<FieldProps>
> = observer(({ ...p }) => {
    const [field, meta, helpers] = useLocalizedField(p.name);
    const error = meta.touched ? meta.error : null;
    const inputProps = {
        ...(p.InputProps || {}),
        startAdornment: p.icon ? <InputAdornment position={"start"}>{p.icon}</InputAdornment> : null,
    };

    const { current: hlprs } = useRef(helpers);

    useEffect(() => {
        const setValue = hlprs.setValue;

        if (p.defaultValue !== undefined) {
            if (p.overrideInitialValue || !meta.initialValue) {
                setValue(p.defaultValue);
            }
        }
    }, [meta.initialValue, p.defaultValue, p.overrideInitialValue, hlprs]);

    const forceLowerCase = {
        onKeyUp: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            helpers.setValue(meta.value.toLowerCase());
        },
    };

    const getHelperText = () => {
        if (!!error) {
            return `${error}`;
        } else {
            return p.helperText || "";
        }
    };

    return (
        <TextField
            fullWidth
            {...p}
            {...field}
            value={meta.value}
            error={!!error}
            label={p.label}
            InputLabelProps={{ id: p.name }}
            inputProps={{
                "aria-labelledby": p.name,
            }}
            helperText={getHelperText()}
            variant={"filled"}
            InputProps={inputProps}
        />
    );
});

// ======================
// FormSwitch
// ======================

interface FormSwitchProps {
    label: string | React.ReactNode;
    name: string;
    helperText?: React.ReactNode;
}

export const FormSwitch: React.FC<FormSwitchProps & Partial<SwitchProps>> = (p) => {
    const { label, name, helperText, ...switchProps } = p;
    const [field, meta] = useLocalizedField(name);
    return (
        <FormControl component={"fieldset"} variant={"standard"} id={name}>
            <FormControlLabel control={<Switch {...switchProps} value={meta.value} checked={meta.value} {...field} />} label={label} />
            <FormHelperText>{helperText || ""}</FormHelperText>
        </FormControl>
    );
};

interface FormCheckboxItemProps {
    label: string | React.ReactNode;
    name: string;
    helperText?: React.ReactNode;
    checkBoxProps?: Partial<CheckboxProps>;
    defaultChecked?: boolean;
    onChangeCustomFn?: (checked: boolean) => void;
}

export const FormCheckboxItem: React.FC<FormCheckboxItemProps & Partial<CheckboxProps> & Partial<FieldProps>> = observer(({ label, helperText, ...p }) => {
    const [field, meta] = useLocalizedField(p.name);
    const error = meta.touched ? meta.error : null;

    return (
        <Box pt={1} pb={1}>
            <FormControlLabel
                id={p.name}
                control={
                    <Checkbox
                        inputProps={{ "aria-labelledby": p.name, id: p.name }}
                        {...p.checkBoxProps}
                        value={meta.value}
                        defaultChecked={p.defaultChecked}
                        checked={meta.value}
                        onChange={(e, checked) => {
                            if (!!p.onChangeCustomFn) {
                                p.onChangeCustomFn(checked);
                            }
                            field.onChange(e);
                        }}
                    />
                }
                label={
                    typeof label === "string" ? (
                        <Typography variant={"body2"} color={"textSecondary"}>
                            {label}
                        </Typography>
                    ) : (
                        label
                    )
                }
            />
            <FormHelperText>{helperText}</FormHelperText>
        </Box>
    );
});

// ======================
// FormSelectableCardsGroup
// ======================

export interface FormSelectableCardConfig extends Omit<SelectableCardProps, "onSelect" | "selected"> {
    value: any;
}

type SharedSelectableCardProps = Omit<
    SelectableCardProps,
    "onSelect" | "selected" | "title" | "description" | "icon" | "tip" | "actions" | "warning" | "chipLabel" | "alert" | "header"
>;

interface FormSelectableCardsGroupProps extends SharedSelectableCardProps {
    options: FormSelectableCardConfig[];
    name: string;
    cardSize?: "xs" | "small" | "medium" | "large";
    maxWidthCardSize?: number;
}

export const FormSelectableCardsGroup: React.FC<FormSelectableCardsGroupProps> = observer((p) => {
    const [field, meta, helpers] = useLocalizedField(p.name);
    const { options, name, cardSize, maxWidthCardSize, ...sharedCardProps } = p;
    return (
        <Box>
            <Grid container spacing={2}>
                {p.options.map((o) => {
                    const { value, ...selectableCardProps } = o;
                    const selected = value === field.value;
                    const onSelect = () => {
                        helpers.setValue(value);
                    };
                    const mergedProps = { ...sharedCardProps, ...selectableCardProps };
                    if (!maxWidthCardSize) {
                        return (
                            <Grid key={value}>
                                <SelectableCard selected={selected} onSelect={onSelect} {...mergedProps} />
                            </Grid>
                        );
                    }
                    return (
                        <Grid
                            key={value}
                            size={{
                                xs: 12,
                                sm: 6,
                                md: maxWidthCardSize,
                            }}
                        >
                            <SelectableCard
                                selected={selected}
                                onSelect={onSelect}
                                {...mergedProps}
                                cardProps={{
                                    ...mergedProps.cardProps,
                                    sx: {
                                        height: "100%",
                                        ...mergedProps.cardProps?.sx,
                                    },
                                }}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
});

// ======================
// FormRadioGroup
// ======================

interface RadioGroupOptionConfig {
    label: string;
    value: any;
    helperText?: React.ReactNode;
    disabled?: boolean;
}

interface FormRadioGroupProps {
    label: string;
    name: string;
    options: RadioGroupOptionConfig[];
    helperText?: React.ReactNode;
    required?: boolean;
}

export const FormRadioGroup: React.FC<FormRadioGroupProps & Partial<FieldProps>> = observer((props) => {
    const [field, meta, helpers] = useLocalizedField(props.name);
    const error = meta.touched ? meta.error : null;
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        helpers.setValue(event.target.value.toString());
    };
    return (
        <FormControl component={"fieldset"} error={!!error} variant={"filled"} fullWidth>
            <FormLabel component={"legend"} error={!!error} required={!!props.required} id={props.name}>
                {props.label}
            </FormLabel>
            <RadioGroup name={props.name} value={field.value.toString()} onChange={handleChange}>
                {props.options.map((option, index) => {
                    return (
                        <Box key={index}>
                            <FormControlLabel
                                disabled={!!option.disabled}
                                id={option.label}
                                value={option.value.toString()}
                                control={<Radio disabled={!!option.disabled} inputProps={{ "aria-labelledby": option.label }} />}
                                label={option.label}
                            />
                            {option.helperText && (
                                <Typography variant={"caption"} color={"textSecondary"}>
                                    {option.helperText}
                                </Typography>
                            )}
                        </Box>
                    );
                })}
            </RadioGroup>
            <FormHelperText error={!!error} variant={"outlined"}>
                {error}
            </FormHelperText>
            <FormHelperText>{props.helperText}</FormHelperText>
        </FormControl>
    );
});

// ======================
// FormSelect
// ======================

interface SelectItemType {
    label: string;
    value: string | number;
}

interface FormSelectProps {
    label: string;
    name: string;
    selectionList: Array<SelectItemType> | Array<string>;
    // render menu item
    listRenderer?: (array: Array<SelectItemType> | Array<string>) => React.ReactNode;
    listFilterer?: (array: Array<SelectItemType> | Array<string>) => Array<SelectItemType> | Array<string>;
    disabled?: boolean;
    required?: boolean;
}

export const FormSelect = observer((props: FormSelectProps & Partial<SelectProps>) => {
    const { selectionList, listRenderer, listFilterer, label, name, required, disabled, ...selectProps } = props;
    const [field, meta, helpers] = useLocalizedField(name);
    const error = meta.touched || !!meta.value ? meta.error : null;
    const { value } = meta;
    const { setValue } = helpers;

    const initialValue = typeof meta.initialValue === "string" ? meta.initialValue : meta.initialValue?.toString();
    const defaultValue = selectProps.defaultValue
        ? typeof selectProps.defaultValue === "string"
            ? selectProps.defaultValue
            : (selectProps.defaultValue as SelectItemType).value.toString()
        : null;

    useEffect(() => {
        if (!initialValue && !!defaultValue) {
            helpers.setValue(defaultValue);
        }
    }, [defaultValue, initialValue, helpers]);

    const filteredList = !!listFilterer ? listFilterer(selectionList) : selectionList;

    const renderList = (list: Array<SelectItemType> | Array<string>) => {
        return list.map((item: SelectItemType | string) => {
            if (typeof item === "string") {
                return (
                    <MenuItem key={item as string} value={item as string}>
                        {item}
                    </MenuItem>
                );
            } else {
                return (
                    <MenuItem key={(item as SelectItemType).value} value={(item as SelectItemType).value}>
                        {(item as SelectItemType).label}
                    </MenuItem>
                );
            }
        });
    };

    const handleChange = (event: SelectChangeEvent<any>, child: ReactNode) => {
        setValue(event.target.value as string);
    };

    return (
        <>
            <FormControl fullWidth>
                <InputLabel variant={"filled"} required={required}>
                    {label}
                </InputLabel>

                <Select
                    multiple={selectProps.multiple}
                    fullWidth
                    {...selectProps}
                    name={name}
                    label={label}
                    disabled={disabled}
                    required={required}
                    sx={{
                        minWidth: 200,
                    }}
                    value={value}
                    onChange={handleChange}
                    variant={"filled"}
                    error={!!error}
                    input={<FilledInput disabled={disabled} required={required} />}
                    MenuProps={{
                        anchorOrigin: {
                            vertical: "bottom",
                            horizontal: "left",
                        },
                        transformOrigin: {
                            vertical: "top",
                            horizontal: "left",
                        },
                    }}
                    inputProps={{
                        "aria-label": label,
                    }}
                >
                    {!!listRenderer ? listRenderer(filteredList) : renderList(filteredList)}
                </Select>
                <FormHelperText error={!!error} variant={"outlined"}>
                    {error}
                </FormHelperText>
            </FormControl>
        </>
    );
});

// ======================
// FormAutocompleteField
// ======================

interface FormAutocompleteFieldProps<OptionType> {
    label: string;
    name: string;
    errorGetter?: (value: any) => string;
    valueGetter?: (value: any) => any;
    customOptionsFilter?: (options: Array<OptionType>) => Promise<Array<OptionType>> | ((options: Array<OptionType>) => Array<OptionType>);
}

export const FormAutocompleteField = observer(
    <OptionType, Multiple extends boolean | undefined, DisableClearable extends boolean | undefined, FreeSolo extends boolean | undefined>(
        props: FormAutocompleteFieldProps<OptionType> & Partial<AutocompleteProps<OptionType, Multiple, DisableClearable, FreeSolo>>
    ) => {
        const { label, name, valueGetter, customOptionsFilter, onChange } = props;
        const [field, meta, helpers] = useLocalizedField(name);
        const { t } = useTranslation();
        const error = meta.touched ? (meta.error ? (props.errorGetter ? props.errorGetter(meta.error) : meta.error) : null) : null;
        const { value } = meta;

        const initialValue = meta.initialValue ? (props.valueGetter ? props.valueGetter(meta.initialValue).toString() : meta.initialValue) : null;
        const defaultValue = props.defaultValue ? (props.valueGetter ? props.valueGetter(props.defaultValue).toString() : props.defaultValue) : null;

        useEffect(() => {
            if (!initialValue && !!defaultValue) {
                helpers.setValue(props.defaultValue);
            }
        }, [defaultValue, initialValue, props.defaultValue, helpers]);

        const handleChange = (
            event: SyntheticEvent<Element, Event>,
            value: any,
            reason: AutocompleteChangeReason,
            details?: AutocompleteChangeDetails<any>
        ) => {
            if (reason === "selectOption") {
                helpers.setValue(value);
                if (onChange) {
                    onChange(event, value, reason, details);
                }
            }
            if (reason === "clear" || reason === "removeOption") {
                helpers.setValue("");
            }
        };

        return (
            <Autocomplete
                noOptionsText={t("common:form.noOptions")}
                defaultValue={props.defaultValue}
                {...props}
                id={name}
                options={props.options}
                value={value}
                onChange={handleChange}
                renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                        {...params}
                        label={label}
                        helperText={error}
                        error={!!error}
                        variant="filled"
                        InputProps={{
                            ...params.InputProps,
                        }}
                    />
                )}
            />
        );
    }
);

interface FormAsyncAutocompleteFieldProps<T, K> {
    label: string;
    name: string;
    dataFetcher: () => Promise<T>;
    errorGetter?: (value: any) => string;
    valueGetter?: (value: any) => any;
    customOptionsFilter?: (options: Array<K>) => Promise<Array<K>> | ((options: Array<K>) => Array<K>);
    required?: boolean;
}

export const FormAsyncAutocompleteField: React.FC<FormAsyncAutocompleteFieldProps<any, any> & Partial<AutocompleteProps<any, any, any, any>>> = observer(
    (props) => {
        const { label, name, dataFetcher, valueGetter, customOptionsFilter, required, ...rest } = props;
        const [field, meta, helpers] = useLocalizedField(name);
        const { t } = useTranslation();
        const error = meta.touched ? (meta.error ? (props.errorGetter ? props.errorGetter(meta.error) : meta.error) : null) : null;

        const handleChange = (event: ChangeEvent<{}>, value: any, reason: AutocompleteChangeReason, details?: AutocompleteChangeDetails<any>) => {
            if (reason === "selectOption") {
                if (!!valueGetter) {
                    helpers.setValue(valueGetter(value));
                } else {
                    helpers.setValue(value);
                }
            }
            if (reason === "clear" || reason === "removeOption") {
                helpers.setValue("");
            }
        };

        const [open, setOpen] = React.useState(false);
        const [options, setOptions] = React.useState([]);
        const [loaded, setLoaded] = React.useState(false);
        const loading = open && !loaded;

        React.useEffect(() => {
            let active = true;
            setLoaded(false);

            (async () => {
                const ListData = new ServerListData<
                    {
                        getItemsList?: () => Array<any>;
                        getPagerMeta?: () => PagerMeta;
                        itemsList?: Array<any>;
                        pagerMeta?: PagerMeta.AsObject;
                    },
                    any
                >(10000).setDataFetcher(dataFetcher);
                const response = await ListData.fetchData();
                if (active && response) {
                    setLoaded(true);
                    if (!!customOptionsFilter) {
                        let filteredOptions;
                        if (!!ListData.data.getItemsList) {
                            filteredOptions = await customOptionsFilter(ListData.data.getItemsList());
                        } else {
                            filteredOptions = await customOptionsFilter(ListData.data.itemsList);
                        }
                        setOptions(filteredOptions);
                    } else {
                        if (!!ListData.data.getItemsList) {
                            setOptions(ListData.data.getItemsList());
                        } else {
                            setOptions(ListData.data.itemsList);
                        }
                    }
                }
            })();

            return () => {
                active = false;
                setLoaded(false);
            };
        }, [open, customOptionsFilter, dataFetcher]);

        React.useEffect(() => {
            if (!open) {
                setOptions([]);
            }
        }, [open]);

        return (
            <>
                <Autocomplete
                    noOptionsText={t("common:form.noOptions")}
                    freeSolo={false}
                    autoSelect={true}
                    {...rest}
                    id={name}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    options={options}
                    onChange={handleChange}
                    loading={loading}
                    renderInput={(params: AutocompleteRenderInputParams) => (
                        <TextField
                            {...params}
                            {...field}
                            required={required}
                            label={label}
                            helperText={error}
                            error={!!error}
                            variant={"filled"}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                ),
                            }}
                        />
                    )}
                />
            </>
        );
    }
);

// ======================
// FormDateTimePicker
// ======================
interface FormDateTimePickerProps {
    label: string;
    name: string;
    helperText?: string;
    defaultValue?: Date;
    getErrorMessage?: (reason: string, value: Date) => string;
}

export const FormDateTimePicker: React.FC<FormTimePickerProps & Partial<MobileDateTimePickerProps<any>>> = observer(({ ...p }) => {
    const { label, name, helperText, defaultValue, getErrorMessage, ...dateTimePickerProps } = p;

    const [field, meta, helpers] = useLocalizedField(p.name);
    const [dateTimePickerError, setDateTimePickerError] = useState("");
    const { t } = useTranslation("validation");

    const error = meta.touched ? meta.error : null;

    useMountEffect(() => {
        if (!meta.initialValue) {
            if (!!p.defaultValue || p.defaultValue === null) {
                helpers.setValue(p.defaultValue);
            }
        }
    });

    const onChange = (date: unknown) => {
        helpers.setValue(date);
    };

    return (
        <MobileDateTimePicker
            slotProps={{
                textField: {
                    error: !!error || !!dateTimePickerError,
                    helperText: dateTimePickerError || error || p.helperText,
                    fullWidth: true,
                    variant: "filled",
                },
            }}
            label={p.label}
            value={meta.value}
            onChange={onChange}
            onError={(reason, value) => {
                let errorMessage = getPickerValidationMessage(reason, t);
                if (!!getErrorMessage) {
                    errorMessage = getErrorMessage(reason, value as Date);
                }
                setDateTimePickerError(errorMessage);
            }}
            {...dateTimePickerProps}
        />
    );
});

// ======================
// FormDateTimePicker
// ======================
interface FormTimePickerProps {
    label: string;
    name: string;
    helperText?: string;
    defaultValue?: Date;
    getErrorMessage?: (reason: string, value: Date) => string;
}

export const FormTimePicker: React.FC<FormTimePickerProps & Partial<MobileTimePickerProps<any, any>>> = observer((p) => {
    const { label, name, helperText, defaultValue, getErrorMessage, ...timePickerProps } = p;
    const [field, meta, helpers] = useLocalizedField(p.name);
    const [timePickerError, setTimePickerError] = useState("");
    const { t } = useTranslation("validation");

    const error = meta.touched ? meta.error : null;

    useMountEffect(() => {
        if (!meta.initialValue) {
            if (!!p.defaultValue || p.defaultValue === null) {
                helpers.setValue(p.defaultValue);
            }
        }
    });

    const onChange = (date: unknown) => {
        helpers.setValue(date);
    };

    return (
        <MobileTimePicker
            value={meta.value}
            onChange={onChange}
            onError={(reason, value) => {
                let errorMessage = getPickerValidationMessage(reason, t);
                if (!!getErrorMessage) {
                    errorMessage = getErrorMessage(reason, value as Date);
                }
                setTimePickerError(errorMessage);
            }}
            slotProps={{
                textField: {
                    error: !!error || !!timePickerError,
                    helperText: timePickerError || error || p.helperText,
                    fullWidth: true,
                    variant: "filled",
                },
            }}
            label={p.label}
            {...timePickerProps}
        />
    );
});

// ======================
// FormTextFileDropzone
// ======================

interface FormTextFileDropzoneProps {
    label: string;
    name: string;
    maxFiles: number;
    accept: string[];
    instructions?: string;
}

export const FormTextFileDropzone: React.FC<FormTextFileDropzoneProps> = observer((p) => {
    const [field, meta, helpers] = useLocalizedField(p.name);
    const [files, setFiles] = useState<File[]>([]);
    const { t } = useTranslation();

    const cardStyle = {
        backgroundColor: "rgba(255, 255, 255, .09)",
        border: "1px solid",
        borderColor: "rgba(255, 255, 255, .7)",
        transition: "background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms",
        cursor: "pointer",
    };

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            setFiles(acceptedFiles);
            acceptedFiles.forEach((file: File) => {
                const reader = new FileReader();
                reader.addEventListener(
                    "load",
                    () => {
                        if (p.maxFiles > 1) {
                            const newArray = (meta.value as any[]).concat([reader.result]);
                            helpers.setValue(newArray);
                        } else {
                            helpers.setValue(reader.result);
                        }
                    },
                    false
                );

                if (file) {
                    reader.readAsText(file);
                }
            });
        },
        [helpers, meta.value, p.maxFiles]
    );

    const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
        accept: p.accept,
        maxFiles: p.maxFiles,
        onDrop,
        maxSize: 5242880,
    });

    const onDeleteFile = (acceptedFiles: File[], index: number) => {
        acceptedFiles.splice(index, 1);
        setFiles([...acceptedFiles]);
        if (!!meta.value.length) {
            helpers.setValue(meta.value.splice(index, 1));
        } else {
            helpers.setValue(null);
        }
    };

    return (
        <>
            {(!files.length || p.maxFiles > 1) && (
                <Box {...getRootProps()} width={"100%"}>
                    <Card
                        sx={{
                            ...cardStyle,
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,.1)",
                            },
                        }}
                    >
                        <Box p={2}>
                            <input {...getInputProps()} />
                            <Typography sx={{ color: "rgba(255,255,255,.7)" }}>
                                {t(p.maxFiles > 1 ? "common:fileDrop.selectMultiple" : "common:fileDrop.selectSingle")}
                            </Typography>
                            <Typography variant={"body2"} sx={{ color: "rgba(255,255,255,.7)" }}>
                                {t("common:fileDrop.acceptedTypes", { types: p.accept.join(", ") })}
                            </Typography>
                        </Box>
                    </Card>
                </Box>
            )}
            {fileRejections.length > 0 && (
                <Box>
                    {fileRejections.map((f, i) => {
                        return (
                            <Box key={i}>
                                <Typography variant={"caption"} sx={{ color: (t: Theme) => t.palette.error.main }}>
                                    {t("common:fileDrop.uploadRejected", {
                                        filename: f.file.name,
                                        reason: f.errors.map((error) => getFileDropErrorMessage(error.code, t)).join(" "),
                                    })}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            )}
            {files.map((f, i) => (
                <Card sx={cardStyle}>
                    <Box p={2} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                        <Typography>{f.name}</Typography>
                        <IconButton onClick={() => onDeleteFile(acceptedFiles, i)} aria-label={t("common:fileDrop.remove", { filename: f.name })}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Card>
            ))}
        </>
    );
});
